import React, { useState, useEffect, useRef } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import UserAuthButton from "../auth/UserAuthButton";
import { useAuthContext } from "../../../context/AuthContext";
import io from "socket.io-client";
import { bindGameResync } from "../../../utils/bindGameResync";
import { describeBuzzerRound } from "../../../utils/buzzerRoundState";

const API_BASE = process.env.REACT_APP_API_URL;

const defaultColors = {
  locked: { bg: "linear-gradient(135deg, #424242, #616161)", border: "#9e9e9e" },
  active: { bg: "linear-gradient(135deg, #00c853, #64dd17)", border: "#00e676" },
  buzzedWinner: { bg: "linear-gradient(135deg, #ffb300, #ff6f00)", border: "#ffca28" },
  buzzedOther: { bg: "linear-gradient(135deg, #ffa726, #ff9800)", border: "#ffb74d" },
  timed_out: { bg: "linear-gradient(135deg, #d50000, #ff1744)", border: "#ff5252" },
};

const playSound = (url) => {
  if (!url || typeof url !== "string" || !url.trim()) return;
  try {
    const audio = new Audio(url.trim());
    audio.play().catch(() => {});
  } catch (_) {}
};

const BuzzerPage = () => {
  const [state, setState] = useState("locked"); // locked | active | buzzed | timed_out
  const [isBuzzing, setIsBuzzing] = useState(false);
  const [, setRoundId] = useState(null);
  const [, setCountdown] = useState(null);
  const [hasBuzzed, setHasBuzzed] = useState(false);
  const [isWinner, setIsWinner] = useState(false);
  const [winnerName, setWinnerName] = useState("");
  const [buzzerConfig, setBuzzerConfig] = useState(null); // colors, audio URLs, image URLs
  const { deviceId } = useAuthContext();
  const configRef = useRef(null); // latest config for use inside socket handlers
  const socketRef = useRef(null);
  const activatedAtRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const appliedRoundRef = useRef(null);
  const deviceIdRef = useRef(deviceId);
  deviceIdRef.current = deviceId;

  useEffect(() => {
    configRef.current = buzzerConfig;
  }, [buzzerConfig]);

  const clearCountdownTimer = () => {
    if (countdownTimerRef.current) {
      clearTimeout(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  };

  const handleBuzz = async () => {
    if (state !== "active" || isBuzzing) return;
    setIsBuzzing(true);

    try {
      const headers = {
        "Content-Type": "application/json",
      };
      if (deviceId) {
        headers["x-device-id"] = deviceId;
      }

      const res = await fetch(`${API_BASE}/api/buzzer/1/buzz`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          latency_ms:
            activatedAtRef.current != null
              ? Date.now() - activatedAtRef.current
              : null,
        }),
      });
      const data = await res.json().catch(() => null);
      if (data?.success) {
        setHasBuzzed(true);
        if (data.is_winner) {
          setIsWinner(true);
        }
        clearCountdownTimer();
        playSound(configRef.current?.buzzed_audio_url);
      }
    } catch (e) {
      console.error("Buzz error", e);
    } finally {
      setIsBuzzing(false);
    }
  };

  const getColors = () => {
    const c = buzzerConfig;
    const hexToGradient = (hex) =>
      hex ? `linear-gradient(135deg, ${hex}, ${hex})` : null;

    if (state === "active" || state === "buzzed") {
      if (isWinner || state === "buzzed") {
        const bg = c?.buzzed_color ? hexToGradient(c.buzzed_color) : null;
        return bg ? { bg, border: c.buzzed_color } : defaultColors.buzzedWinner;
      }
      if (hasBuzzed) {
        const bg = c?.buzzed_color ? hexToGradient(c.buzzed_color) : null;
        return bg ? { bg, border: c.buzzed_color } : defaultColors.buzzedOther;
      }
      const bg = c?.active_color ? hexToGradient(c.active_color) : null;
      return bg ? { bg, border: c.active_color } : defaultColors.active;
    }

    if (state === "timed_out") {
      const bg = c?.timed_out_color ? hexToGradient(c.timed_out_color) : null;
      return bg ? { bg, border: c.timed_out_color } : defaultColors.timed_out;
    }

    const bg = c?.locked_color ? hexToGradient(c.locked_color) : null;
    return bg ? { bg, border: c.locked_color } : defaultColors.locked;
  };

  const getImageUrl = () => {
    const c = buzzerConfig;
    if (!c) return null;
    if (state === "timed_out" && c.timed_out_image_url) return c.timed_out_image_url;
    if (state === "active" || state === "buzzed") {
      if (isWinner || hasBuzzed || state === "buzzed") return c.buzzed_image_url || null;
      return c.active_image_url || null;
    }
    return c.locked_image_url || null;
  };

  const { bg, border } = getColors();
  const stateImageUrl = getImageUrl();

  // Load buzzer config (colors, audio, images) on mount
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/buzzer/1/status`)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && data?.success && data?.config) {
          const cfg = data.config;
          setBuzzerConfig({
            locked_color: cfg.locked_color,
            active_color: cfg.active_color,
            buzzed_color: cfg.buzzed_color,
            timed_out_color: cfg.timed_out_color,
            locked_audio_url: cfg.locked_audio_url || "",
            active_audio_url: cfg.active_audio_url || "",
            buzzed_audio_url: cfg.buzzed_audio_url || "",
            timed_out_audio_url: cfg.timed_out_audio_url || "",
            locked_image_url: cfg.locked_image_url || "",
            active_image_url: cfg.active_image_url || "",
            buzzed_image_url: cfg.buzzed_image_url || "",
            timed_out_image_url: cfg.timed_out_image_url || "",
          });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Socket setup — reconnect and tab-return pull the current round from the server.
  useEffect(() => {
    const socket = io(API_BASE, {
      transports: ["websocket"],
    });
    socketRef.current = socket;

    const applyConfig = (cfg) => {
      if (!cfg) return;
      setBuzzerConfig({
        locked_color: cfg.locked_color,
        active_color: cfg.active_color,
        buzzed_color: cfg.buzzed_color,
        timed_out_color: cfg.timed_out_color,
        locked_audio_url: cfg.locked_audio_url || "",
        active_audio_url: cfg.active_audio_url || "",
        buzzed_audio_url: cfg.buzzed_audio_url || "",
        timed_out_audio_url: cfg.timed_out_audio_url || "",
        locked_image_url: cfg.locked_image_url || "",
        active_image_url: cfg.active_image_url || "",
        buzzed_image_url: cfg.buzzed_image_url || "",
        timed_out_image_url: cfg.timed_out_image_url || "",
      });
    };

    let syncGen = 0;
    const syncRound = async () => {
      const gen = ++syncGen;
      try {
        const res = await fetch(`${API_BASE}/api/buzzer/1/status`);
        const data = await res.json();
        if (gen !== syncGen || !data?.success) return;
        applyConfig(data.config);
        const described = describeBuzzerRound(data.config, data.lastRound, deviceIdRef.current);
        if (described.state === "active") {
          if (appliedRoundRef.current === described.roundId && countdownTimerRef.current) return;
          appliedRoundRef.current = described.roundId;
          clearCountdownTimer();
          setState("active");
          setRoundId(described.roundId);
          setCountdown(described.countdown);
          setHasBuzzed(false);
          setIsWinner(false);
          setWinnerName("");
          activatedAtRef.current = Date.now();
          const ms = Math.max(0, described.countdown) * 1000;
          countdownTimerRef.current = setTimeout(() => {
            countdownTimerRef.current = null;
            setCountdown(0);
            setState("timed_out");
          }, ms);
          return;
        }
        clearCountdownTimer();
        appliedRoundRef.current = described.roundId;
        setState(described.state);
        setRoundId(described.roundId);
        setCountdown(described.countdown);
        setWinnerName(described.winnerName);
        setIsWinner(described.isWinner);
        if (described.state !== "active") setHasBuzzed(described.isWinner);
      } catch (err) {
        console.warn("Buzzer resync failed", err);
      }
    };

    socket.on("connect", () => {
      syncRound();
    });
    const unbindResync = bindGameResync(syncRound);

    socket.on("buzzer.activated", (payload) => {
      syncGen += 1;
      appliedRoundRef.current = payload.round_id;
      clearCountdownTimer();
      setState("active");
      setRoundId(payload.round_id);
      activatedAtRef.current = Date.now();
      setHasBuzzed(false);
      setIsWinner(false);
      setWinnerName("");
      playSound(configRef.current?.active_audio_url);
      const seconds = payload.countdown_seconds ?? 10;
      setCountdown(seconds);
      countdownTimerRef.current = setTimeout(() => {
        countdownTimerRef.current = null;
        setCountdown(0);
        setState("timed_out");
        playSound(configRef.current?.timed_out_audio_url);
      }, seconds * 1000);
    });

    socket.on("buzzer.locked", () => {
      syncGen += 1;
      appliedRoundRef.current = null;
      clearCountdownTimer();
      setState("locked");
      setRoundId(null);
      setCountdown(null);
      setHasBuzzed(false);
      setIsWinner(false);
      setWinnerName("");
      playSound(configRef.current?.locked_audio_url);
    });

    socket.on("buzzer.timeout", () => {
      syncGen += 1;
      appliedRoundRef.current = null;
      clearCountdownTimer();
      setCountdown(0);
      setState("timed_out");
      playSound(configRef.current?.timed_out_audio_url);
    });

    socket.on("buzzer.attempt", (payload) => {
      if (payload.is_winner) {
        syncGen += 1;
        clearCountdownTimer();
        setWinnerName(payload.display_name || "Someone");
        playSound(configRef.current?.buzzed_audio_url);
      }
    });

    return () => {
      unbindResync();
      clearCountdownTimer();
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#000",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <AppBar
        position="static"
        sx={{ bgcolor: "rgba(0,0,0,0.9)", borderBottom: "1px solid #333" }}
      >
        <Toolbar>
          <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
            <Box
              component="img"
              src="/beatTheBuzzer2.png"
              alt="Beat the Buzzer"
              sx={{
                height: { xs: 32, sm: 40 },
                objectFit: "contain",
              }}
            />
          </Box>
          <UserAuthButton />
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: 4,
          background:
            "radial-gradient(circle at 50% 0%, rgba(255,0,255,0.3), transparent 55%), radial-gradient(circle at 0% 100%, rgba(0,229,255,0.25), transparent 55%)",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            mb: 4,
            fontWeight: 700,
            textAlign: "center",
            textShadow: "0 0 18px rgba(0,0,0,0.9)",
          }}
        >
          Beat the Buzzer
        </Typography>

        <Box
          sx={{
            width: 260,
            height: 260,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: bg,
            ...(stateImageUrl
              ? {
                  backgroundImage: `url(${stateImageUrl})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }
              : {}),
            boxShadow:
              state === "active"
                ? "0 0 40px rgba(0, 230, 118, 0.8)"
                : state === "buzzed"
                ? "0 0 40px rgba(255, 202, 40, 0.8)"
                : state === "timed_out"
                ? "0 0 40px rgba(255, 82, 82, 0.8)"
                : "0 0 30px rgba(158, 158, 158, 0.4)",
            border: `4px solid ${border}`,
            position: "relative",
            transition: "all 0.25s ease-out",
          }}
        >
          <Button
            onClick={handleBuzz}
            disabled={state !== "active" || isBuzzing}
            sx={{
              width: 170,
              height: 170,
              borderRadius: "50%",
              bgcolor: "#000",
              color: "#fff",
              fontWeight: 800,
              fontSize: "1.4rem",
              textTransform: "uppercase",
              letterSpacing: "0.12em",
              boxShadow: "0 16px 30px rgba(0,0,0,0.8)",
              border: "3px solid rgba(255,255,255,0.2)",
              "&:hover": {
                bgcolor: "#111",
              },
              "&.Mui-disabled": {
                bgcolor: "rgba(0,0,0,0.8)",
                color: "rgba(255,255,255,0.4)",
                borderColor: "rgba(255,255,255,0.14)",
              },
            }}
          >
            {isBuzzing ? <CircularProgress size={28} sx={{ color: "#fff" }} /> : "BUZZ"}
          </Button>
        </Box>

        <Typography
          variant="body1"
          sx={{ mt: 3, color: "rgba(255,255,255,0.8)", textAlign: "center" }}
        >
          {state === "locked" && "Waiting for host to start the round."}
          {state === "active" && "Tap as fast as you can when you know the answer!"}
          {state === "buzzed" &&
            (isWinner
              ? "You buzzed in first! Waiting for host decision..."
              : winnerName
              ? `${winnerName} buzzed in first. Wait for the next question.`
              : "Someone buzzed in first. Wait for the next question.")}
          {state === "timed_out" && "Time's up. Waiting for the next round."}
        </Typography>
      </Box>
    </Box>
  );
};

export default BuzzerPage;

